/**
 * AM35 协议转换层
 * 处理上位机与前端之间的数据格式转换
 */

/**
 * 转换上报的 KeyMatrix 数据为前端格式
 * 将键盘按键类型（type=6）中的空值转换为空字符串
 * 因为 consumerKey 和 sdtKey 互斥，只有一方有值
 * 
 * 上报格式：[6, consumerKey, sdtKey, combo]
 * 例如：[6, "0x0000", "0x04", "1000"] -> [6, "", "0x04", "1000"]
 *       [6, "0x0192", "0x00", "0000"] -> [6, "0x0192", "", "0000"]
 * 
 * @param {Object} keyMatrix - 上报的 KeyMatrix 数据
 * @returns {Object} 转换后的 KeyMatrix 数据
 */
export function parseKeyMatrixFromDevice(keyMatrix) {
  if (!keyMatrix) return keyMatrix;
  
  const parsed = {};
  for (const key in keyMatrix) {
    const mapping = keyMatrix[key];
    // 检查是否是键盘按键类型（type=6）且有4个元素
    if (Array.isArray(mapping) && (mapping[0] == 6 || mapping[0] === '6') && mapping.length >= 4) {
      const consumerKey = mapping[1];
      const sdtKey = mapping[2];
      
      // 判断是否为空值
      const isConsumerKeyEmpty = !consumerKey || consumerKey === '0x0000' || consumerKey === '0x00';
      const isSdtKeyEmpty = !sdtKey || sdtKey === '0x00' || sdtKey === '0x0000';
      
      parsed[key] = [
        mapping[0],
        isConsumerKeyEmpty ? '' : consumerKey,
        isSdtKeyEmpty ? '' : sdtKey,
        mapping[3] || '0000',
      ];
    } else {
      parsed[key] = mapping;
    }
  }
  return parsed;
}

function cloneMacroPayload(payload) {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map((item) => {
    if (item && typeof item === "object") {
      return { ...item };
    }
    return item;
  });
}

/**
 * 从 KeyMatrix 中补齐宏列表。
 * 设备初始化时可能只回 KeyMatrix，不回 mouseMacroData，
 * 这里从宏绑定项 [8, repeat_mode, repeat_times, macro_name, payload]
 * 反推出最小可用的 mouseMacroData，避免初始化后宏绑定回显为空。
 *
 * @param {Object} keyMatrix - 已标准化后的 KeyMatrix
 * @param {Array} mouseMacroData - 宿主已返回的宏列表（可为空）
 * @returns {Array} 合并后的 mouseMacroData
 */
export function mergeMouseMacroDataFromKeyMatrix(keyMatrix, mouseMacroData) {
  const mergedMouseMacroData = Array.isArray(mouseMacroData)
    ? mouseMacroData.map((macro) => ({
        ...macro,
        macroValue: {
          ...macro?.macroValue,
          keyOrder: cloneMacroPayload(macro?.macroValue?.keyOrder),
        },
      }))
    : [];

  if (!keyMatrix || typeof keyMatrix !== "object") {
    return mergedMouseMacroData;
  }

  const existingMacroNames = new Set(
    mergedMouseMacroData
      .map((macro) => macro?.macro_name)
      .filter((macroName) => typeof macroName === "string" && macroName)
  );

  Object.entries(keyMatrix).forEach(([_keyId, mapping]) => {
    const isMacroBinding =
      Array.isArray(mapping) &&
      (mapping[0] == 8 || mapping[0] === "8") &&
      typeof mapping[3] === "string" &&
      mapping[3];

    if (!isMacroBinding || existingMacroNames.has(mapping[3])) {
      return;
    }
    mergedMouseMacroData.push({
      index: mergedMouseMacroData.length,
      macro_name: mapping[3],
      macroValue: {
        keyOrder: cloneMacroPayload(mapping[4]),
      },
      keyID: "0",
      toKeyID: "8",
      repeat_mode: Number(mapping[1] ?? 0),
      repeat_times: Number(mapping[2] ?? 1),
    });
    existingMacroNames.add(mapping[3]);
  });

  return mergedMouseMacroData;
}
